import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, Loader2 } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import 'rrweb-player/dist/style.css';

interface SessionReplayPlayerProps {
  sessionId: string;
  events: any[];
}

export function SessionReplayPlayer({ sessionId, events }: SessionReplayPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [replayer, setReplayer] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!containerRef.current || events.length === 0) return;

    const initPlayer = async () => {
      try {
        const { Replayer } = await import('rrweb');

        // Clear container
        containerRef.current!.innerHTML = '';

        const replayerInstance = new Replayer(events, {
          root: containerRef.current!,
          speed: 1,
          skipInactive: true,
          showWarning: false,
          showDebug: false,
        });

        // Calculate duration
        const firstEvent = events[0];
        const lastEvent = events[events.length - 1];
        const totalDuration = lastEvent.timestamp - firstEvent.timestamp;
        setDuration(totalDuration);

        // Listen to replay events
        replayerInstance.on('finish', () => {
          setIsPlaying(false);
          setCurrentTime(totalDuration);
        });

        setReplayer(replayerInstance);
        setLoading(false);
      } catch (error) {
        console.error('Error initializing replayer:', error);
        setLoading(false);
      }
    };

    initPlayer();

    return () => {
      if (replayer) {
        replayer.destroy();
      }
    };
  }, [events]);

  const handlePlayPause = () => {
    if (!replayer) return;

    if (isPlaying) {
      replayer.pause();
    } else {
      replayer.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    if (!replayer) return;
    replayer.pause();
    setCurrentTime(0);
    setIsPlaying(false);
    replayer.play(0);
    setTimeout(() => replayer.pause(), 10);
  };

  const handleSeek = (value: number[]) => {
    if (!replayer) return;
    const newTime = value[0];
    replayer.pause();
    replayer.play(newTime);
    setCurrentTime(newTime);
    setTimeout(() => {
      if (!isPlaying) {
        replayer.pause();
      }
    }, 10);
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Update current time while playing
  useEffect(() => {
    if (!replayer || !isPlaying) return;

    const interval = setInterval(() => {
      const meta = replayer.getMetaData();
      setCurrentTime(meta.currentTime);
    }, 100);

    return () => clearInterval(interval);
  }, [replayer, isPlaying]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Session Replay</CardTitle>
        <CardDescription>Watch the complete user session: {sessionId}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div
              ref={containerRef}
              className="border rounded-lg overflow-hidden bg-background"
              style={{ minHeight: '400px' }}
            />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePlayPause}
                  disabled={!replayer}
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={handleRestart} disabled={!replayer}>
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
              <Slider
                value={[currentTime]}
                onValueChange={handleSeek}
                max={duration}
                step={100}
                disabled={!replayer}
                className="w-full"
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
